'use client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { SearchInput } from '@/app/components/shared/SearchInput';
import { Tabs, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { usePathname, useRouter } from '@/i18n/routing';
import { type Template, type TemplateCategory, templatesList, templatesListCategories } from './apis';
import { Feed } from './Feed';
import { ProStudioBlack } from './icons/ProStudioBlack';
import { ProStudioWhite } from './icons/ProStudioWhite';
import { getTemplateAll } from './utils';

const PAGE_SIZE = 36;

export const Templates: React.FC = () => {
  const t = useTranslations('templates');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templatesCategories, setTemplatesCategories] = useState<TemplateCategory[]>([]);
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState<string>('all');

  const [hasMore, setHasMore] = useState(false);
  const pageTokenRef = useRef<string>('');
  const searchParams = useSearchParams();
  const from = searchParams.get('from');
  const router = useRouter();
  const pathname = usePathname();
  const [searchValue, setSearchValue] = useState<string>('');

  const { run: getTemplates, loading: templatesLoading } = useFetch(
    (selectedTemplateCategory?: string) =>
      templatesList({
        query: {
          'pager.page_token': pageTokenRef.current,
          'pager.pagesz': PAGE_SIZE,
          category: selectedTemplateCategory === 'all' ? undefined : selectedTemplateCategory,
          ...(searchValue ? { scene_name: searchValue } : { use_rec: true }),
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          toast({
            type: 'warning',
            message: error.message,
          });
        }
        if (data) {
          if (data.next_page_token) {
            setHasMore(true);
          } else {
            setHasMore(false);
          }
          setTemplates([...templates, ...data.templates]);
          pageTokenRef.current = data.next_page_token || '';
        }
      },
    },
  );

  const { run: getTemplatesCategories, loading: templatesCategoriesLoading } = useFetch(
    () =>
      templatesListCategories({
        query: {
          recommend_scope: 'list',
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          toast({
            type: 'warning',
            message: error.message,
          });
        }
        if (data?.categories) {
          setTemplatesCategories([...getTemplateAll(t), ...data.categories]);
        }
      },
    },
  );

  useEffect(() => {
    setHasMore(false);
    pageTokenRef.current = '';
    getTemplatesCategories();
    setTemplates([]);
    getTemplates(selectedTemplateCategory);
  }, [selectedTemplateCategory, searchValue]);

  useEffect(() => {
    if (from) {
      setSelectedTemplateCategory(from);
    }
  }, [from]);

  useEffect(() => {
    if (templatesCategories.length === 0) {
      return;
    }

    const hasSelectedTemplateCategory = templatesCategories.find(
      (category) => category.id === selectedTemplateCategory,
    );

    if (!hasSelectedTemplateCategory?.id && selectedTemplateCategory !== 'all') {
      toast({
        type: 'warning',
        message: t('errorMessage'),
      });
      setSelectedTemplateCategory('all');
    }
  }, [selectedTemplateCategory, templatesCategories]);

  const renderTemplateIcon = (option: TemplateCategory) => {
    if (option.name !== 'ProStudio' && option.name !== '专业工作室') {
      return null;
    }

    if (option.id === selectedTemplateCategory) {
      return <ProStudioWhite className="mr-1 size-4" />;
    }
    return <ProStudioBlack className="mr-1 size-4" />;
  };

  return (
    <div className="max-content md:mt-4">
      <div className="sticky top-0 z-20 flex items-center justify-between bg-system-bg08 pb-6">
        <div>
          {templatesCategories.length > 0 && (
            <div className="hide-scrollbar flex w-full overflow-x-auto bg-system-bg01 text-xl md:top-10 lg:top-10">
              <Tabs
                defaultValue="all"
                value={selectedTemplateCategory}
                onValueChange={(value) => {
                  setSelectedTemplateCategory(value);
                  router.push(`${pathname}?from=${value}`);
                }}
              >
                <TabsList className="flex w-full gap-2 bg-transparent p-0">
                  {templatesCategories.map((option) => {
                    return (
                      <TabsTrigger
                        key={option.id}
                        className="h-7 min-w-[62px] rounded-8 bg-system-white/10 px-3 py-2 font-normal text-sm text-white capitalize data-[state=active]:bg-system-white data-[state=active]:text-system-black"
                        value={option.id || 'all'}
                      >
                        {renderTemplateIcon(option)}
                        {option.name}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </Tabs>
            </div>
          )}
        </div>
        <SearchInput
          className="sm:hidden"
          defaultValue={searchValue}
          onSearch={setSearchValue}
          placeholder={t('search.placeholder')}
        />
      </div>
      <div className="min-h-[calc(100vh-200px)] bg-system-bg01">
        <Feed
          templates={templates}
          hasMore={hasMore}
          selectedTemplateCategory={selectedTemplateCategory}
          next={getTemplates}
          loading={templatesLoading || templatesCategoriesLoading}
          isSearch={!!searchValue}
        />
      </div>
    </div>
  );
};
